import { useState } from "react";
import { Navigate } from "react-router-dom";
import { Building2, Lock, User, KeyRound, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const DEMO_ACCOUNTS = [
  { label: "Süper Admin", username: "superadmin", password: "Admin123!", office_code: "", role: "SUPER_ADMIN" },
  { label: "Broker", username: "broker", password: "Demo123!", office_code: "TR-34-DEMO", role: "BROKER" },
  { label: "Muhasebe", username: "muhasebe", password: "Demo123!", office_code: "TR-34-DEMO", role: "ACCOUNTANT" },
  { label: "Danışman", username: "danisman", password: "Demo123!", office_code: "TR-34-DEMO", role: "CONSULTANT" },
  { label: "Temsilci", username: "temsilci", password: "Demo123!", office_code: "", role: "REPRESENTATIVE" },
  { label: "Platform Muhasebe", username: "platformmuhasebe", password: "Demo123!", office_code: "", role: "PLATFORM_ACCOUNTANT" },
];

export default function LoginPage() {
  const { user, login } = useAuth();
  const [officeCode, setOfficeCode] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/panel" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login({ office_code: officeCode || null, username, password });
      toast.success("Giriş başarılı");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Giriş başarısız");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = (d) => {
    setOfficeCode(d.office_code);
    setUsername(d.username);
    setPassword(d.password);
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left: visual */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #0D131A 0%, #1a2332 100%)" }}>
        <img
          src="https://images.unsplash.com/photo-1682184805271-11671b7ecf4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjByZWFsJTIwZXN0YXRlJTIwaW50ZXJpb3IlMjBhcmNoaXRlY3R1cmUlMjBhcGFydG1lbnR8ZW58MHx8fHwxNzkzMjk4NTY5fDA&ixlib=rb-4.1.0&q=85"
          alt="Luxury property"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Building2 className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>EmlakPlus</span>
          </div>
          <div>
            <h1 className="text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
              Bölgesel Emlak<br />Yönetim Platformu
            </h1>
            <p className="text-slate-300 text-lg max-w-md">
              Portföy, muhasebe, araç takibi ve müşteri yönetimi — tek bir modern panelde.
            </p>
          </div>
          <div className="text-slate-400 text-sm">© 2026 EmlakPlus</div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold tracking-tight">EmlakPlus</span>
          </div>
          <h2 className="text-3xl font-bold tracking-tight mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Giriş Yap</h2>
          <p className="text-slate-500 mb-8">Ofis kodunuz, kullanıcı adınız ve şifrenizle sisteme giriş yapın.</p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="office_code" className="text-slate-700 mb-1.5 block">Emlak Ofis Kodu</Label>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="office_code"
                  data-testid="login-office-code-input"
                  value={officeCode}
                  onChange={(e) => setOfficeCode(e.target.value)}
                  placeholder="Örn: TR-34-DEMO (Süper admin boş bırakabilir)"
                  className="pl-10 h-11"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="username" className="text-slate-700 mb-1.5 block">Kullanıcı Adı</Label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="kullaniciadi"
                  required
                  className="pl-10 h-11"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 mb-1.5 block">Şifre</Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 h-11"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Giriş Yap"}
            </Button>
          </form>

          <div className="mt-8 pt-6 border-t border-slate-200">
            <p className="text-xs uppercase tracking-wider text-slate-500 mb-3 font-semibold">Demo Hesapları (Hızlı Giriş)</p>
            <div className="grid grid-cols-2 gap-2">
              {DEMO_ACCOUNTS.map((d) => (
                <button
                  key={d.username}
                  type="button"
                  data-testid={`demo-${d.role.toLowerCase()}-btn`}
                  onClick={() => fillDemo(d)}
                  className="text-left px-3 py-2 rounded-lg border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
                >
                  <div className="text-xs font-semibold text-slate-900">{d.label}</div>
                  <div className="text-xs text-slate-500">{d.username}</div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
